export default `# Contributions

The contributions panel is where you review, accept, or ignore avatar suggestions. Think of it as a curation queue — avatars generate ideas, and you decide which ones make it into the session.

---

## Status Counters

Three counters at the top show:
- **Pending** (blue, pulsing) — Suggestions awaiting your review
- **Accepted** (green) — Ideas you've approved
- **Ignored** (gray) — Dismissed suggestions

Use the filter and sort controls to organise the queue by avatar, urgency, or time.

---

## Requesting Contributions

At the top of the panel:
- **Ask All** — Request a new contribution from every avatar
- **Ask Selected** — Choose specific avatars to prompt

---

## Reviewing Pending Contributions

Each pending suggestion card shows:
- **Avatar name and role** with coloured indicator
- **Urgency badge**:
  - 🔴 **Urgent** — High-priority suggestion (pulsing)
  - 🟡 **Important** — Worth reviewing soon
  - 🔵 **Normal** — Standard suggestion
- **Timestamp** — When the suggestion arrived
- **Suggestion text** — The avatar's idea
- **Governor note** — If the AI oversight system flagged anything (shown in gray italic)

### Actions

| Button | Effect |
|--------|--------|
| **Accept** | Add the suggestion to the session |
| **Accept & Speak** | Accept and play as audio via text-to-speech |
| **Ignore** | Dismiss the suggestion |
| **Stage** | Pre-generate TTS audio before accepting |
| **Spotlight** | Highlight on the second screen display |

---

## Accepted & Ignored History

Below the pending queue:
- **Accepted History** — Shows previously approved contributions with a green border. Items marked "Live on Main Screen" are currently displayed.
- **Ignored** — Grayed-out dismissed suggestions. Click **Restore** to move them back to pending.

---

## Key Concepts

- **Staging** — Pre-generating text-to-speech audio so it's ready to play immediately when accepted. Useful for smooth facilitation.
- **Spotlight** — Sends a contribution to the [Second Screen](help:session-second-screen) for the room to see.
- **Governor** — An AI oversight layer that flags contributions for quality or relevance issues.

---

## Tips

- **Stage audio for important contributions** — Pre-generate TTS so there's no delay when you accept
- **Use Spotlight for key moments** — Putting a contribution on the second screen draws the room's attention
- **Don't ignore too aggressively** — You can always restore ignored suggestions, but reviewing them keeps the discussion diverse
- **Sort by urgency** when the queue is long — urgent items often contain time-sensitive insights`;
